import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer } from "react-toastify";
import Spinners from "components/Common/Spinner";
import {
  createUserByOrganizer,
  getUsersByOrganizer,
  getAllEventByOrganizer,
  deleteUserByOrganizer,
  updateUserByOrganizer,
  getUserByEmail,
  getAllOrganizer,
} from "api";
import TableContainer from "components/Common/TableContainer";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import toast, { Toaster } from 'react-hot-toast';

const userTypeToRole = {
  scanner: 4,
  "second-admin": 10,
  "event-second-admin": 8,
};

const AddSubUser = () => {
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [organizersList, setOrganizersList] = useState([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [eventsList, setEventsList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [existingUserRoles, setExistingUserRoles] = useState([]);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const response = await getAllOrganizer();
        if (Array.isArray(response)) setOrganizersList(response);
      } catch {
        toast.error("Failed to load organizers");
      }
    };
    fetchOrganizers();
  }, []);

  useEffect(() => {
    if (selectedOrganizer?.value) getUserList(selectedOrganizer.value);
    else setUsers([]);
  }, [selectedOrganizer]);

  useEffect(() => {
    if (modal && selectedOrganizer?.value) getOnlyOrganizerEvents();
  }, [modal]);

  const getUserList = async (orgId) => {
    setIsLoading(true);
    try {
      const response = await getUsersByOrganizer(orgId);
      if (response) setUsers(response);
    } catch {}
    setIsLoading(false);
  };

  const getOnlyOrganizerEvents = async () => {
    setEventsList([]);
    try {
      const response = await getAllEventByOrganizer();
      if (Array.isArray(response)) {
        const now = new Date();
        const active = response
          .filter((e) => new Date(e.eventDate) >= now)
          .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
          .filter((e) => e.eventStatus === "active" || e.eventStatus === "published");
        setEventsList(active);
      }
    } catch {}
  };

  const toggle = () => {
    if (!modal) {
      formik.resetForm();
      setUserExists(false);
      setExistingUserRoles([]);
    }
    setModal((p) => !p);
  };

  const toggleEditModal = () => setEditModal((p) => !p);

  const handleEmailBlur = async (e) => {
    const email = e.target.value;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setIsCheckingEmail(true);
    try {
      const response = await getUserByEmail(email);
      if (response?.userId) {
        setUserExists(true);
        const roles = response.userRoles?.length > 0
          ? response.userRoles.map(Number)
          : response.userRole
          ? [Number(response.userRole)]
          : [];
        setExistingUserRoles(roles);
        formik.setFieldValue("name", response.name || "");
        formik.setFieldValue("phoneNo", response.phoneNo || "");
        formik.setFieldValue("selectedRoles", roles);
        formik.setFieldValue(
          "eventId",
          Array.isArray(response.eventId) ? response.eventId[0] : response.eventId || ""
        );
        toast.info("User already exists. Details pre-filled.");
      } else {
        const currentEmail = formik.values.email;
        formik.resetForm();
        formik.setFieldValue("email", currentEmail);
        setUserExists(false);
        setExistingUserRoles([]);
      }
    } catch {
      setUserExists(false);
      setExistingUserRoles([]);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleRoleChange = (e) => {
    formik.setFieldValue("role", Number(e.target.value));
    formik.setFieldValue("eventId", "");
    formik.setFieldValue("venueId", "");
  };

  const handleRoleToggle = (roleValue) => {
    const roles = [...formik.values.selectedRoles];
    const idx = roles.indexOf(roleValue);
    idx > -1 ? roles.splice(idx, 1) : roles.push(roleValue);
    formik.setFieldValue("selectedRoles", roles);
  };

  const handleEditRoleToggle = (roleValue) => {
    const roles = [...editFormik.values.selectedRoles];
    const idx = roles.indexOf(roleValue);
    idx > -1 ? roles.splice(idx, 1) : roles.push(roleValue);
    editFormik.setFieldValue("selectedRoles", roles);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNo: "",
      role: "",
      selectedRoles: [] ,
      eventId: "",
      venueId: "",
      password: "",
      status: "active",
    },
    validationSchema: Yup.lazy(() =>
      Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phoneNo: Yup.string().required("Phone number is required"),
        selectedRoles: Yup.array().min(1, "Select at least one role").required("Role is required"),
        password: !userExists
          ? Yup.string().min(6, "Minimum 6 characters").required("Password is required")
          : Yup.string().notRequired(),
      })
    ),
    onSubmit: async (values) => {
      if (!selectedOrganizer?.value) {
        toast.error("Please select an organizer first");
        return;
      }
      setIsSubmitting(true);
      try {
        const payload = {
          organizerId: selectedOrganizer.value,
          name: values.name,
          email: values.email,
          phoneNo: values.phoneNo,
          password: values.password,
          status: values.status,
          userRole: Number(values.selectedRoles[0]),
          userRoles: userExists
            ? Array.from(new Set([...existingUserRoles, ...values.selectedRoles.map(Number)]))
            : values.selectedRoles.map(Number),
          ...(values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && values.eventId
            ? { eventId: [values.eventId] }
            : {}),
        };
        await createUserByOrganizer(selectedOrganizer.value, payload);
        toast.success("User added successfully");
        setModal(false);
        getUserList(selectedOrganizer.value);
        formik.resetForm();
        setUserExists(false);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to add user");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const editFormik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNo: "",
      role: "",
      selectedRoles: [] ,
      eventId: "",
      venueId: "",
      status: "active",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phoneNo: Yup.string().required("Phone number is required"),
      selectedRoles: Yup.array().min(1, "Select at least one role").required("Role is required"),
      status: Yup.string().oneOf(["active", "inactive"]).required("Status is required"),
    }),
    onSubmit: async (values) => {
      if (!selectedUser || !selectedOrganizer?.value) return;
      setIsSubmitting(true);
      try {
        const payload = {
          organizerId: selectedOrganizer.value,
          name: values.name,
          email: values.email,
          phoneNo: values.phoneNo,
          status: values.status,
          userRole: Number(values.selectedRoles[0]),
          userRoles: values.selectedRoles.map(Number),
          ...(values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && values.eventId
            ? { eventId: [values.eventId] }
            : {}),
        };
        if (values.venueId) payload.venueId = values.venueId;
        await createUserByOrganizer(selectedOrganizer.value, payload);
        toast.success("User updated successfully");
        toggleEditModal();
        getUserList(selectedOrganizer.value);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to update user");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const getRoleDisplay = (user) => {
    const roles =
      Array.isArray(user.userRoles) && user.userRoles.length > 0
        ? user.userRoles
        : [user.userRole];
    return (
      roles
        .map((roleId) => {
          const entry = Object.entries(userTypeToRole).find(([_, v]) => v === Number(roleId));
          return entry
            ? entry[0].split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
            : null;
        })
        .filter(Boolean)
        .join(", ") || "N/A"
    );
  };



  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          const user = cell.row.original;
          return (
            <div style={{ maxWidth: "220px" }}>
              <h6
                className="font-size-14 mb-1 text-truncate"
                style={{
                  maxWidth: "240px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={cell.getValue()}
              >
                <Link to="#" className="text-dark">
                  {cell.getValue()}
                </Link>
              </h6>
              <small className="text-muted d-block text-truncate">
                {user.email}
              </small>
              <small className="text-muted d-block text-truncate">
                {user.phoneNo}
              </small>
            </div>
          );
        }
      },
      {
        header: 'Role',
        accessorKey: 'userRole',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          return <span className="badge badge-soft-info font-size-11">{getRoleDisplay(cellProps.row.original)}</span>;
        }
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          const user = cell.row.original;
          return (
            <span className={`badge badge-soft-${user.status === 'active' ? 'success' : 'danger'} font-size-11`}>
              {user.status}
            </span>
          );
        }
      },
      {
        header: 'Created Date',
        accessorKey: 'createdAt',
        enableColumnFilter: true,
        enableSorting: true,
        cell: (cellProps) => {
          return <span>{new Date(cellProps.getValue()).toLocaleDateString()}</span>;
        }
      },
      {
        header: 'Action',
        cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              <div
                className="text-success btn-hover cursor-pointer"
                id={`edit-${item.userId}`}
                onClick={() => {
                  setSelectedUser(item);
                  editFormik.setValues({
                    name: item.name || "",
                    email: item.email || "",
                    phoneNo: item.phoneNo || "",
                    role: item.userRole || "",
                    selectedRoles: Array.isArray(item.userRoles)
                      ? item.userRoles.map(Number)
                      : item.userRole
                      ? [Number(item.userRole)]
                      : [],
                    eventId: Array.isArray(item.eventId) ? item.eventId[0] : item.eventId || "",
                    venueId: item.venueId || "",
                    status: item.status || "active",
                  });
                  toggleEditModal();
                }}
              >
                <i className="mdi mdi-pencil font-size-18" />
                <UncontrolledTooltip placement="top" target={`edit-${item.userId}`}>
                  Edit
                </UncontrolledTooltip>
              </div>

            </div>
          );
        }
      }
    ],
    [selectedOrganizer]
  );

  const selectedOrgName = selectedOrganizer?.label || "";

  const roleCheckboxes = (
    values,
    onToggle,
    touchedField,
    errorField,
    submitCount,
    disabledRoles = []
  ) => (
    <FormGroup>
      <Label>Select Roles</Label>
      <div className="d-flex flex-column gap-2 mt-2">
        {Object.entries(userTypeToRole).map(([label, roleId]) => (
          <FormGroup check key={roleId}>
            <Label check>
              <Input
                type="checkbox"
                checked={values.includes(roleId)}
                disabled={disabledRoles.includes(roleId)}
                onChange={() => onToggle(roleId)}
              />{" "}
              {label === "scanner"
                ? "Scanner Boy"
                : label.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
            </Label>
          </FormGroup>
        ))}
      </div>
      {(touchedField || submitCount > 0) && errorField && (
        <div className="text-danger small mt-1">{String(errorField)}</div>
      )}
    </FormGroup>
  );

  return (
    <React.Fragment>
      <Toaster />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Manage Sub Users" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col xl="5">
                      <Select
                        value={selectedOrganizer}
                        placeholder="Select Organizer"
                        onChange={(data) => setSelectedOrganizer(data)}
                        options={organizersList.map((org) => ({
                          value: org.organizerId,
                          label: `${org.name || org.organizerName} (${org.email})`,
                        }))}
                        className="select2-selection"
                      />
                    </Col>
                    <Col xl="7" className="text-end">
                      <Button
                        color="success"
                        className="btn-rounded waves-effect waves-light mb-2"
                        onClick={toggle}
                        disabled={!selectedOrganizer}
                      >
                        <i className="mdi mdi-plus me-1"></i> New Sub User
                      </Button>
                    </Col>
                  </Row>

                  {!selectedOrganizer ? (
                    <p className="text-center text-muted">Select an organizer to view sub users.</p>
                  ) : isLoading ? (
                    <Spinners setLoading={setIsLoading} />
                  ) : users.length === 0 ? (
                    <p className="text-center w-100">No sub users found for this organizer.</p>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={users}
                      isGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search sub users..."
                      isCustomPageSize={true}
                      isAddButton={false}
                      tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer autoClose={2000} />

      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          Add Sub User{selectedOrgName ? ` — ${selectedOrgName}` : ""}
        </ModalHeader>
        <ModalBody className="p-4">
          <Form onSubmit={formik.handleSubmit}>
            <FormGroup>
              <Label for="email">Email</Label>
              <div className="position-relative">
                <Input
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Enter user email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  invalid={(formik.touched.email || formik.submitCount > 0) && !!formik.errors.email}
                />
                 {isCheckingEmail && (
                  <div className="position-absolute" style={{ top: "50%", right: "10px", transform: "translateY(-50%)" }}>
                    <Spinner size="sm" color="secondary" />
                  </div>
                )}
                <FormFeedback>{formik.errors.email}</FormFeedback>
              </div>
            </FormGroup>

            <FormGroup>
              <Label for="name">Name</Label>
              <Input
                type="text"
                name="name"
                id="name"
                placeholder="Enter user name"
                value={formik.values.name}
                onChange={formik.handleChange}
                invalid={(formik.touched.name || formik.submitCount > 0) && !!formik.errors.name}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="phoneNo">Phone Number</Label>
              <Input
                type="text"
                name="phoneNo"
                id="phoneNo"
                placeholder="Enter phone number"
                value={formik.values.phoneNo}
                onChange={formik.handleChange}
                invalid={(formik.touched.phoneNo || formik.submitCount > 0) && !!formik.errors.phoneNo}
              />
              <FormFeedback>{formik.errors.phoneNo}</FormFeedback>
            </FormGroup>

            {roleCheckboxes(
              formik.values.selectedRoles,
              handleRoleToggle,
              formik.touched.selectedRoles ,
              formik.errors.selectedRoles,
              formik.submitCount,
              existingUserRoles
            )}

            {formik.values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && (
              <FormGroup>
                <Label for="eventId">Event</Label>
                <Input
                  type="select"
                  name="eventId"
                  id="eventId"
                  value={formik.values.eventId}
                  onChange={formik.handleChange}
                >
                  <option value="">Select an event</option>
                  {eventsList.map((event) => (
                    <option key={event.eventId} value={event.eventId}>
                      {event.eventName}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}

            {!userExists && (
              <FormGroup>
                <Label for="password">Password</Label>
                <div className="position-relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Enter password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    invalid={(formik.touched.password || formik.submitCount > 0) && !!formik.errors.password}
                  />
                  <i
                    className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"} position-absolute`}
                    style={{ top: "50%", right: "10px", transform: "translateY(-50%)", cursor: "pointer", color: "#888" }}
                    onClick={() => setShowPassword((p) => !p)}
                  />
                  <FormFeedback>{formik.errors.password}</FormFeedback>
                </div>
              </FormGroup>
            )}

            <ModalFooter>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Loading..." : userExists ? "Update User" : "Add User"}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Sub User</ModalHeader>
        <ModalBody className="p-4">
          <Form onSubmit={editFormik.handleSubmit}>
            <FormGroup>
              <Label>Email</Label>
              <Input type="email" name="email" value={editFormik.values.email} disabled />
            </FormGroup>

            <FormGroup>
              <Label for="edit-name">Name</Label>
              <Input
                type="text"
                name="name"
                id="edit-name"
                value={editFormik.values.name}
                onChange={editFormik.handleChange}
                invalid={(editFormik.touched.name || editFormik.submitCount > 0) && !!editFormik.errors.name}
              />
              <FormFeedback>{editFormik.errors.name}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="edit-phoneNo">Phone Number</Label>
              <Input
                type="text"
                name="phoneNo"
                id="edit-phoneNo"
                value={editFormik.values.phoneNo}
                onChange={editFormik.handleChange}
                invalid={(editFormik.touched.phoneNo || editFormik.submitCount > 0) && !!editFormik.errors.phoneNo}
              />
              <FormFeedback>{editFormik.errors.phoneNo}</FormFeedback>
            </FormGroup>

            {roleCheckboxes(
              editFormik.values.selectedRoles,
              handleEditRoleToggle,
              editFormik.touched.selectedRoles ,
              editFormik.errors.selectedRoles,
              editFormik.submitCount
            )}

            {editFormik.values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && (
              <FormGroup>
                <Label for="edit-eventId">Event</Label>
                <Input
                  type="select"
                  name="eventId"
                  id="edit-eventId"
                  value={editFormik.values.eventId}
                  onChange={editFormik.handleChange}
                >
                  <option value="">Select an event</option>
                  {eventsList.map((event) => (
                    <option key={event.eventId} value={event.eventId}>
                      {event.eventName}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}

            <FormGroup>
              <Label for="edit-status">Status</Label>
              <Input
                type="select"
                name="status"
                id="edit-status"
                value={editFormik.values.status}
                onChange={editFormik.handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Input>
            </FormGroup>

            <ModalFooter>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Loading..." : "Update User"}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AddSubUser;